import { collection, addDoc, deleteDoc, doc, getDocs, query, updateDoc, serverTimestamp, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '@/firebase';
import { Notification } from '@/types/notification';
import { v4 as uuidv4 } from 'uuid';

export class NotificationsData {
  // Fetch all notifications for a user
  static async fetchNotifications(userId: string): Promise<Notification[]> {
    if (!userId || !db) {
      throw new Error('User not authenticated or database not available');
    }

    try {
      const notificationsCollection = collection(db, 'users', userId, 'notifications');
      const notificationsQuery = query(notificationsCollection);
      const querySnapshot = await getDocs(notificationsQuery);
      
      const notificationsData: Notification[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as Omit<Notification, 'id'>;
        notificationsData.push({
          id: doc.id,
          ...data,
        });
      });
      
      // Sort notifications by creation date (newest first)
      return notificationsData.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.toMillis() - a.createdAt.toMillis();
      });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  // Add a new notification
  static async addNotification(
    userId: string,
    notification: Omit<Notification, 'id' | 'createdAt'>
  ): Promise<Notification> {
    if (!userId || !db) {
      throw new Error('User not authenticated or database not available');
    }

    try {
      const notificationsCollection = collection(db, 'users', userId, 'notifications');
      const docRef = await addDoc(notificationsCollection, {
        ...notification,
        isRead: false,
        createdAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        ...notification,
        createdAt: Timestamp.now(),
      };
    } catch (error) {
      console.error('Error adding notification:', error);
      throw error;
    }
  }

  // Mark a notification as read
  static async markAsRead(userId: string, id: string): Promise<void> {
    if (!userId || !db) {
      throw new Error('User not authenticated or database not available');
    }

    try {
      const notificationDocRef = doc(db, 'users', userId, 'notifications', id);
      await updateDoc(notificationDocRef, {
        isRead: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Mark a notification as unread
  static async markAsUnread(userId: string, id: string): Promise<void> {
    if (!userId || !db) {
      throw new Error('User not authenticated or database not available');
    }

    try {
      const notificationDocRef = doc(db, 'users', userId, 'notifications', id);
      await updateDoc(notificationDocRef, {
        isRead: false
      });
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      throw error;
    }
  }

  // Remove a notification
  static async removeNotification(userId: string, id: string): Promise<void> {
    if (!userId || !db) {
      throw new Error('User not authenticated or database not available');
    }

    try {
      const notificationDocRef = doc(db, 'users', userId, 'notifications', id);
      await deleteDoc(notificationDocRef);
    } catch (error) {
      console.error('Error removing notification:', error);
      throw error;
    }
  }

  // Mark all notifications as read
  static async markAllAsRead(userId: string): Promise<void> {
    if (!userId || !db) {
      throw new Error('User not authenticated or database not available');
    }

    try {
      const notificationsCollection = collection(db, 'users', userId, 'notifications');
      const notificationsQuery = query(notificationsCollection);
      const querySnapshot = await getDocs(notificationsQuery);
      
      const batch = writeBatch(db);
      
      querySnapshot.forEach((document) => {
        const notificationRef = doc(db!, 'users', userId, 'notifications', document.id);
        batch.update(notificationRef, { isRead: true });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  // Get demo notifications (for fallback or testing)
  static getDemoNotifications(): Notification[] {
    return [
      {
        id: uuidv4(),
        title: 'Budget Alert',
        message: 'You have exceeded your dining budget this month by $50.',
        type: 'warning',
        isRead: false,
        createdAt: Timestamp.now(),
      },
      {
        id: uuidv4(),
        title: 'Payment Due',
        message: 'Credit card payment of $250 is due in 3 days.',
        type: 'error',
        isRead: false,
        createdAt: Timestamp.now(),
      },
      {
        id: uuidv4(),
        title: 'Budget Goal Achieved',
        message: 'Congratulations! You achieved your savings goal of $1,000 this month.',
        type: 'success',
        isRead: true,
        createdAt: Timestamp.now(),
      },
      {
        id: uuidv4(),
        title: 'New Feature Available',
        message: 'Try our new budget forecasting tool to predict your future expenses.',
        type: 'info',
        isRead: false,
        createdAt: Timestamp.now(),
      },
      {
        id: uuidv4(),
        title: 'Recurring Bill',
        message: 'Your monthly Netflix subscription ($14.99) will be charged tomorrow.',
        type: 'info',
        isRead: true,
        createdAt: Timestamp.now(),
      },
      {
        id: uuidv4(),
        title: 'Unusual Activity',
        message: 'We detected a large transaction of $500 at Amazon.com. Was this you?',
        type: 'warning',
        isRead: false,
        createdAt: Timestamp.now(),
      }
    ];
  }

  // Initialize test notifications for a user
  static async initializeTestNotifications(userId: string): Promise<void> {
    if (!userId || !db) {
      throw new Error('User not authenticated or database not available');
    }

    const testNotifications = [
      {
        title: 'Welcome to Budget App',
        message: 'Thank you for joining! Start by setting up your monthly budget.',
        type: 'info',
        isRead: false,
        createdAt: serverTimestamp(),
      },
      {
        title: 'New Feature Available',
        message: 'Try our new budget forecasting tool to predict your future expenses.',
        type: 'info',
        isRead: false,
        createdAt: serverTimestamp(),
      },
      {
        title: 'Budget Alert',
        message: 'You have exceeded your dining budget this month by $50.',
        type: 'warning',
        isRead: false,
        createdAt: serverTimestamp(),
      },
      {
        title: 'Payment Due',
        message: 'Credit card payment of $250 is due in 3 days.',
        type: 'error',
        isRead: false,
        createdAt: serverTimestamp(),
      }
    ];

    try {
      const batch = writeBatch(db);
      const notificationsCollection = collection(db, 'users', userId, 'notifications');

      // Add each notification to the batch
      for (const notification of testNotifications) {
        const docRef = doc(notificationsCollection);
        batch.set(docRef, notification);
      }

      // Commit the batch
      await batch.commit();
      console.log('Test notifications initialized successfully');
    } catch (error) {
      console.error('Error initializing test notifications:', error);
      throw error;
    }
  }
} 